import {
  type SideBySideConsultNoteCandidate,
  type SideBySideConsultNoteConfirmResponse,
} from '@heidi/session/contracts';
import { z } from 'zod';

const jsonParseIfJson = (input: unknown) => {
  if (typeof input !== 'string') return input;
  try {
    return JSON.parse(input) as unknown;
  } catch {
    return input; // leave as string if not valid JSON
  }
};

const typedStream = z.object({
  type: z.enum(['final_result']).catch('final_result'),
  data: z.string(),
});
const stringStream = z.string();

/**
 * This schema will parse and coerce:
 *  1. "hello" -> { type: 'final_result', data: 'hello' }
 *  2. "{ type: 'final_result', data: 'hello' }" -> { type: 'final_result', data: 'hello' }
 *  3. { type: 'final_result', data: 'hello' } -> { type: 'final_result', data: 'hello' }
 */
export const streamSchema = z.preprocess(
  jsonParseIfJson,
  z.union([typedStream, stringStream]).transform((val) => {
    if (typeof val === 'string') {
      return { type: 'final_result', data: val } as z.infer<typeof typedStream>;
    }
    return val;
  })
);

export type StreamType = z.infer<typeof streamSchema>;

export enum SideBySideStreamStatus {
  'WAITING_START' = 'WAITING_START',
  'STREAMING' = 'STREAMING',
  'DONE' = 'DONE',
  'ERROR' = 'ERROR',
}

export type WithStream = {
  stream: StreamType[];
  status: SideBySideStreamStatus;
  abortController: AbortController;
};

export type WithSelection = {
  hovered: boolean;
  selected: boolean;
  confirmed: boolean;
};

export type WithTimings = {
  timings: {
    startTime: Date | null;
    endTime: Date | null;
    firstChunkTime: Date | null;
    mostRecentChunkTime: Date | null;
  };
};

export type WithFeedback = {
  feedback: {
    positiveFeedback: string;
    negativeFeedback: string;
  };
};

export type SideBySideConsultNoteCandidateWithStream = SideBySideConsultNoteCandidate &
  WithSelection &
  WithStream &
  WithTimings &
  WithFeedback;

export type SideBySideConsultNoteFreeTrialPromotion = Omit<
  SideBySideConsultNoteConfirmResponse,
  'promote_free_trial'
> & { promote_free_trial: true; sessionId: string };
